import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Typography,
  Link,
  CircularProgress,
  Alert,
  Fade,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';

// Import custom components
import TermsAndPrivacy from './TermsAndPrivacy';

// Styled components
const FormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2)
}));

const PasswordStrengthIndicator = styled(Box)(({ theme, strength }) => {
  const getColor = () => {
    switch (strength) {
      case 'weak':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      case 'strong':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[300];
    }
  };

  return {
    height: 4,
    width: '100%',
    backgroundColor: theme.palette.grey[200],
    borderRadius: 2,
    position: 'relative',
    marginTop: theme.spacing(1),
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: strength === 'weak' ? '33%' : strength === 'medium' ? '66%' : strength === 'strong' ? '100%' : '0%',
      backgroundColor: getColor(),
      borderRadius: 2,
      transition: 'width 0.3s ease'
    }
  };
});

const StepperContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3)
}));

const RegistrationForm = ({ onSubmit, loading, error, clearError }) => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'recruiter',
    school: '',
    department: '',
    agreeToTerms: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    confirmPassword: false,
    school: false,
    department: false
  });

  // Email validation
  const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation
  const getPasswordStrength = (password) => {
    if (!password) return '';
    if (password.length < 6) return 'weak';
    if (password.length < 10) return 'medium';
    return 'strong';
  };

  // Password match validation
  const doPasswordsMatch = () => {
    return formData.password === formData.confirmPassword;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    const newValue = name === 'agreeToTerms' ? checked : value;
    setFormData({ ...formData, [name]: newValue });
    
    // Clear errors when user starts typing
    if (formError) setFormError('');
    if (error) clearError();
  };

  // Handle input blur
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Toggle confirm password visibility
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Handle next step
  const handleNext = () => {
    // Validate current step
    if (activeStep === 0) {
      if (!formData.firstName || !formData.lastName || !formData.email || !isEmailValid(formData.email)) {
        setFormError('Please fill in all required fields with valid information');
        // Mark fields as touched to show validation errors
        setTouched({
          ...touched,
          firstName: true,
          lastName: true,
          email: true
        });
        return;
      }
    } else if (activeStep === 1) {
      if (!formData.password || formData.password.length < 6 || !formData.confirmPassword || !doPasswordsMatch()) {
        setFormError(
          !formData.password || formData.password.length < 6 
            ? 'Password must be at least 6 characters' 
            : !doPasswordsMatch() 
              ? 'Passwords do not match' 
              : 'Please fill in all required fields'
        );
        // Mark fields as touched to show validation errors
        setTouched({
          ...touched,
          password: true,
          confirmPassword: true
        });
        return;
      }
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setFormError('');
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setFormError('');
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Final validation
    if (!formData.agreeToTerms) {
      setFormError('You must agree to the terms and conditions');
      return;
    }
    
    if (!formData.role) {
      setFormError('Please select a role');
      return;
    }
    
    // Submit the form
    onSubmit({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      role: formData.role,
      school: formData.school,
      department: formData.department
    });
  };

  // Registration steps
  const steps = [
    {
      label: 'Personal Information',
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="firstName"
              label="First Name"
              name="firstName"
              autoComplete="given-name"
              autoFocus
              value={formData.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.firstName && !formData.firstName}
              helperText={touched.firstName && !formData.firstName ? 'First name is required' : ' '}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="lastName"
              label="Last Name"
              name="lastName"
              autoComplete="family-name"
              value={formData.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.lastName && !formData.lastName}
              helperText={touched.lastName && !formData.lastName ? 'Last name is required' : ' '}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && (!formData.email || !isEmailValid(formData.email))}
              helperText={
                touched.email && !formData.email 
                  ? 'Email is required' 
                  : touched.email && !isEmailValid(formData.email) 
                    ? 'Please enter a valid email' 
                    : ' '
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Security',
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && (!formData.password || formData.password.length < 6)}
              helperText={
                touched.password && !formData.password 
                  ? 'Password is required' 
                  : touched.password && formData.password.length < 6 
                    ? 'Password must be at least 6 characters' 
                    : ' '
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          {/* Password strength indicator */}
          {formData.password && (
            <Grid item xs={12}>
              <PasswordStrengthIndicator strength={getPasswordStrength(formData.password)} />
              <Typography variant="caption" color="text.secondary">
                Password strength: {getPasswordStrength(formData.password) || 'none'}
              </Typography>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.confirmPassword && (!formData.confirmPassword || !doPasswordsMatch())}
              helperText={
                touched.confirmPassword && !formData.confirmPassword 
                  ? 'Please confirm your password' 
                  : touched.confirmPassword && !doPasswordsMatch() 
                    ? 'Passwords do not match' 
                    : ' '
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={toggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Role & Organization',
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                id="role"
                name="role"
                value={formData.role}
                label="Role"
                onChange={handleChange}
                startAdornment={
                  <InputAdornment position="start">
                    <WorkIcon color="action" />
                  </InputAdornment>
                }
              >
                <MenuItem value="recruiter">Recruiter</MenuItem>
                <MenuItem value="coach">Coach</MenuItem>
                <MenuItem value="assistant">Assistant</MenuItem>
                <MenuItem value="admin">Administrator</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="school"
              label="School/Organization"
              name="school"
              value={formData.school}
              onChange={handleChange}
              onBlur={handleBlur}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SchoolIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="department"
              label="Department/Team"
              name="department"
              value={formData.department}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <TermsAndPrivacy 
              onAccept={() => setFormData({ ...formData, agreeToTerms: true })}
              onDecline={() => setFormData({ ...formData, agreeToTerms: false })}
            />
          </Grid>
        </Grid>
      )
    }
  ];

  return (
    <FormContainer component="form" onSubmit={handleSubmit}>
      {/* Error message */}
      {(error || formError) && (
        <Fade in={Boolean(error || formError)}>
          <Alert 
            severity="error" 
            sx={{ width: '100%' }}
            onClose={() => formError ? setFormError('') : clearError()}
          >
            {formError || error}
          </Alert>
        </Fade>
      )}
      
      {/* Registration stepper */}
      <StepperContainer>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>
                <Box sx={{ mb: 2 }}>
                  {step.content}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      variant="outlined"
                    >
                      Back
                    </Button>
                    {index === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={loading || !formData.agreeToTerms}
                        startIcon={loading ? null : <PersonAddIcon />}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Create Account'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
        
        {/* Final step completion */}
        {activeStep === steps.length && (
          <Paper square elevation={0} sx={{ p: 3 }}>
            <Typography>All steps completed - you&apos;re ready to create your account!</Typography>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              sx={{ mt: 1, mr: 1 }}
              disabled={loading || !formData.agreeToTerms}
              startIcon={loading ? null : <PersonAddIcon />}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>
          </Paper>
        )}
      </StepperContainer>
    </FormContainer>
  );
};

export default RegistrationForm;
